// src/pages/MarketInsightsPage.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { artBasel2025Service } from '../services/artBasel/artBasel2025.service';
import './ArtBaselInsights.css';

export const MarketInsightsPage = () => {
  const { t, i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const locale = language.startsWith("de") ? "de-DE" : language.startsWith("en") ? "en-US" : "fr-FR";
  const translateCountry = (name) => t(`marketInsights.countries.${name}`, { defaultValue: name });
  const translateSector = (name) => t(`marketInsights.sectorNames.${name}`, { defaultValue: name });
  const [metrics, setMetrics] = useState(null);
  const [, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState('overview');

  useEffect(() => {
    const loadArtBaselData = async () => {
      try {
        setLoading(true);
        
        // Charger les métriques
        const adminMetrics = artBasel2025Service.getAdminMetrics();
        setMetrics(adminMetrics);
        
        // Charger les tendances
        const trendData = await artBasel2025Service.getArtBasel2025Trends();
        setTrends(trendData);
        
      } catch (error) {
        console.error('Erreur chargement données Art Basel:', error);
      } finally {
        setLoading(false);
      }
    };

    loadArtBaselData();
  }, [language]);

  const renderOverview = () => (
    <div className="overview-section">
      <h2>📈 {t("marketInsights.overview.title")}</h2>
      
      <div className="market-overview-grid">
        <div className="metric-card highlight">
          <h3>{t("marketInsights.overview.globalSales")}</h3>
          <div className="metric-value">${metrics.marketOverview.globalSales}B</div>
          <div className="metric-subtitle">{t("marketInsights.overview.billionUsd")}</div>
        </div>
        
        <div className="metric-card">
          <h3>{t("marketInsights.overview.onlineShare")}</h3>
          <div className="metric-value">{metrics.marketOverview.onlineShare}%</div>
          <div className="metric-change positive">{t("marketInsights.overview.vs2024")}</div>
        </div>
        
        <div className="metric-card">
          <h3>{t("marketInsights.overview.volumeGrowth")}</h3>
          <div className="metric-value">+{metrics.marketOverview.volumeGrowth}%</div>
          <div className="metric-subtitle">{t("marketInsights.overview.transactions")}</div>
        </div>
        
        <div className="metric-card">
          <h3>{t("marketInsights.overview.averageValue")}</h3>
          <div className="metric-value">${Math.round(metrics.marketOverview.avgTransactionValue).toLocaleString(locale)}</div>
          <div className="metric-subtitle">{t("marketInsights.overview.perTransaction")}</div>
        </div>
      </div>

      <div className="top-markets">
        <h3>🌍 {t("marketInsights.overview.topMarkets")}</h3>
        <div className="markets-grid">
          {metrics.marketOverview.topMarkets.map((market, index) => (
            <div key={index} className="market-item">
              <div className="market-country">{translateCountry(market.country)}</div>
              <div className="market-share">{market.share}%</div>
              <div className={`market-performance ${market.performance < 0 ? 'negative' : 'positive'}`}>
                {market.performance > 0 ? '+' : ''}{market.performance}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSectors = () => (
    <div className="sectors-section">
      <h2>🎨 {t("marketInsights.sectors.title")}</h2>
      
      <div className="sectors-grid">
        {metrics.sectorPerformance.map((sector, index) => (
          <div key={index} className="sector-card">
            <div className="sector-header">
              <h3>{translateSector(sector.sector)}</h3>
              <div className="sector-share">{sector.share}%</div>
            </div>
            
            <div className="sector-metrics">
              <div className="sector-performance">
                <span className="label">{t("marketInsights.sectors.performance")}</span>
                <span className={`value ${sector.performance < 0 ? 'negative' : 'positive'}`}>
                  {sector.performance > 0 ? '+' : ''}{sector.performance}%
                </span>
              </div>
              
              <div className="sector-volume">
                <span className="label">{t("marketInsights.sectors.volume")}</span>
                <span className="value positive">{sector.volume_change}</span>
              </div>
            </div>
            
            <div className="sector-insight">
              <p>{sector.insight}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPriceSegments = () => (
    <div className="price-segments-section">
      <h2>💰 {t("marketInsights.prices.title")}</h2>
      
      <div className="price-segments-grid">
        {Object.entries(metrics.priceSegments).map(([segment, data], index) => (
          <div key={index} className="price-segment-card">
            <h3>{segment.replace(/_/g, ' ').replace('under', '< $').replace('over', '> $').replace('between', '$').replace('k', 'K').replace('M', 'M')}</h3>
            
            <div className="segment-metrics">
              <div className="segment-metric">
                <span className="label">{t("marketInsights.prices.volumeShare")}</span>
                <span className="value">{data.share_volume}%</span>
              </div>
              
              <div className="segment-metric">
                <span className="label">{t("marketInsights.prices.valueShare")}</span>
                <span className="value">{data.share_value}%</span>
              </div>
              
              <div className="segment-metric">
                <span className="label">{t("marketInsights.prices.growth")}</span>
                <span className={`value ${data.growth.startsWith('+') ? 'positive' : 'negative'}`}>
                  {data.growth}
                </span>
              </div>
            </div>
            
            <div className="segment-insight">
              <p>{data.insight}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderRecommendations = () => (
    <div className="recommendations-section">
      <h2>💡 {t("marketInsights.recommendations.title")}</h2>
      
      <div className="recommendations-list">
        {metrics.recommendations.map((rec, index) => (
          <div key={index} className="recommendation-item">
            <div className="recommendation-icon">🎯</div>
            <div className="recommendation-text">{rec}</div>
          </div>
        ))}
      </div>
      
      <div className="collector-insights">
        <h3>👥 {t("marketInsights.recommendations.collectorInsights")}</h3>
        <div className="insights-grid">
          <div className="insight-item">
            <div className="insight-value">{metrics.collectorInsights.avg_allocation_art}%</div>
            <div className="insight-label">{t("marketInsights.recommendations.artAllocation")}</div>
          </div>
          
          <div className="insight-item">
            <div className="insight-value">{metrics.collectorInsights.online_adoption}%</div>
            <div className="insight-label">{t("marketInsights.recommendations.onlinePreference")}</div>
          </div>
          
          <div className="insight-item">
            <div className="insight-value">{metrics.collectorInsights.demographics?.millennials_participation ?? metrics.collectorInsights.millennials_participation}%</div>
            <div className="insight-label">{t("marketInsights.recommendations.millennials")}</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t("marketInsights.loading")}</p>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="error-container">
        <p>{t("marketInsights.loadError")}</p>
      </div>
    );
  }

  return (
    <div className="artbasel-insights-page">
      <header className="page-header">
        <h1>🎨 {t("marketInsights.title")}</h1>
        <p>{t("marketInsights.subtitle")}</p>
      </header>

      <nav className="section-nav">
        <button 
          className={selectedSection === 'overview' ? 'active' : ''}
          onClick={() => setSelectedSection('overview')}
        >
          {t("marketInsights.nav.overview")}
        </button>
        <button 
          className={selectedSection === 'sectors' ? 'active' : ''}
          onClick={() => setSelectedSection('sectors')}
        >
          {t("marketInsights.nav.sectors")}
        </button>
        <button 
          className={selectedSection === 'prices' ? 'active' : ''}
          onClick={() => setSelectedSection('prices')}
        >
          {t("marketInsights.nav.prices")}
        </button>
        <button 
          className={selectedSection === 'recommendations' ? 'active' : ''}
          onClick={() => setSelectedSection('recommendations')}
        >
          {t("marketInsights.nav.recommendations")}
        </button>
      </nav>

      <main className="insights-content">
        {selectedSection === 'overview' && renderOverview()}
        {selectedSection === 'sectors' && renderSectors()}
        {selectedSection === 'prices' && renderPriceSegments()}
        {selectedSection === 'recommendations' && renderRecommendations()}
      </main>

      <footer className="insights-footer">
        <p>{t("marketInsights.footer.source", { version: metrics.algorithm_version })}</p>
        <p>{t("marketInsights.footer.updated", { date: metrics.market_data_freshness })}</p>
      </footer>
    </div>
  );
};

export default MarketInsightsPage;